import React from 'react'
import { Button, type ButtonProps, styled } from '@mui/material'
import { type PaletteColor } from '@mui/material/styles'

import '~components/styles/global.less'

declare module '@mui/material/Button' {
  interface ButtonPropsVariantOverrides {
    link: true
  }
}

interface StyledButtonProps extends ButtonProps {
  boxShadow?: boolean
  size?: 'small' | 'medium' | 'large'
  variant?: 'contained' | 'outlined' | 'text' | 'link'
  maxWidth?: string
  target?: string
  rel?: string
}

const StyledButton = styled(Button)<StyledButtonProps>(({
  theme,
  boxShadow,
  color = 'secondary',
  variant,
  size,
  maxWidth,
}) => {
  const getColor = (): string => {
    if (variant === 'contained') {
      return (theme.palette[color as keyof typeof theme.palette] as PaletteColor)?.contrastText
    }
    return (theme.palette[color as keyof typeof theme.palette] as PaletteColor)?.main
  }

  const buttonColor = getColor() || theme.palette.text.primary

  if (variant === 'link') {
    return {
      padding: 0,
      minWidth: 0,
      backgroundColor: 'transparent',
      color: buttonColor,
      textDecoration: 'underline',
      textDecorationThickness: '1px',
      textUnderlineOffset: '2px',
      '&:hover': {
        backgroundColor: 'transparent',
        color: color === 'primary' ? theme.palette.secondary.main : theme.palette.primary.main,
        textDecoration: 'underline',
        textDecorationThickness: '2px',
      },
      '&:active': {
        backgroundColor: 'transparent',
      },
      '&:focus-visible': {
        backgroundColor: theme.palette.primary.contrastText,
        textDecoration: 'underline',
        textDecorationThickness: '2px',
      },
    }
  }

  const baseStyles = {
    display: 'inline-flex',
    maxWidth: maxWidth || (size === 'small' ? '25%' : size === 'large' ? '100%' : '50%'),
    padding: size === 'small' ? '4px 10px' : '7px 24px',
    gap: '10px',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    borderRadius: '4px',
    boxShadow: boxShadow
      ? 'rgba(60, 64, 67, 0.08) 0px 1px 3px 0px, rgba(130, 130, 130, 0.15) 0px 1px 3px 3px'
      : 'none',
    transition: 'all 0.2s ease-in-out',
    color: buttonColor,
    '&::before': { display: 'none' },
  }

  if (variant === 'outlined') {
    return {
      ...baseStyles,
      border: `1px solid ${buttonColor}`,
      backgroundColor: theme.palette.secondary.contrastText,
      '&:hover': {
        backgroundColor: theme.palette.action.hover,
        opacity: 0.8,
        border: `1px solid ${buttonColor}`,
      },
      '&:active': {
        boxShadow: 'none',
        transform: 'scale(0.95)',
        border: `1px solid ${buttonColor}`,
        backgroundColor: theme.palette.secondary.contrastText,
      },
    }
  }

  return {
    ...baseStyles,
    backgroundColor:
      variant === 'contained'
        ? (theme.palette[color as keyof typeof theme.palette] as PaletteColor)?.main
        : variant === 'text'
          ? 'transparent'
          : theme.palette.secondary.contrastText,
    '&:hover': {
      backgroundColor:
        variant === 'contained'
          ? (theme.palette[color as keyof typeof theme.palette] as PaletteColor)?.main
          : variant === 'text'
            ? color === 'primary'
              ? '#067EB10D'
              : '#0000000D'
            : theme.palette.action.hover,
      opacity: variant === 'text' ? 1 : 0.8,
      transition: 'opacity 0.2s ease-in-out',
    },
    '&:active': {
      boxShadow: 'none',
      transform: 'scale(0.95)',
      color: buttonColor,
      backgroundColor:
        variant === 'contained'
          ? (theme.palette[color as keyof typeof theme.palette] as PaletteColor)?.main
          : variant === 'text'
            ? 'transparent'
            : theme.palette.secondary.contrastText,
    },
  }
})

const CustomButton: React.FC<StyledButtonProps> = ({
  boxShadow,
  children,
  variant,
  className,
  ...props
}) => {
  return (
    <StyledButton
      boxShadow={boxShadow}
      variant={variant}
      {...props}
      className={`double-blue-border-on-focus ${className || ''}`.trim()}
    >
      {children}
    </StyledButton>
  )
}

export default CustomButton
